import { Fragment } from 'react';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

import type { DeviceCodeBlockingPolicy } from '../api/waves';
import type { DeviceCodeBlockState } from '../lib/deviceCodeBlocking';
import { IDENTIFIER, STATUS, TEXT } from '../theme/tokens';

// FD-592 (FD-ADR-047 §2, §3) — two notices shared by the activation dialog and
// the Activate preflight page, so the two surfaces cannot word the same fact two
// ways.

function PolicyNames({ policies }: { policies: DeviceCodeBlockingPolicy[] }) {
  return (
    <>
      {policies.map((p, i) => (
        <Fragment key={p.id || p.displayName}>
          {i > 0 && (i === policies.length - 1 ? ' and ' : ', ')}
          <span style={IDENTIFIER}>{p.displayName}</span>
        </Fragment>
      ))}
    </>
  );
}

/**
 * What the named device-code probe said, in words.
 *
 * `prominent` is true where the operator has chosen (or is about to choose) device
 * code; there the "checked, clear" and "could not check" states are worth a line.
 * Elsewhere only a BLOCK is worth interrupting for. `not-asked` (a Manager that
 * predates FD-591) renders nothing anywhere — silence about an unasked question is
 * not "unknown".
 */
export function DeviceCodeBlockNotice({
  state,
  prominent,
}: {
  state: DeviceCodeBlockState;
  prominent: boolean;
}) {
  if (state.kind === 'not-asked') return null;

  if (state.kind === 'unknown') {
    if (!prominent) return null;
    return (
      <p className="font-display text-[12.5px]" style={{ color: TEXT.secondary }} data-testid="device-code-probe">
        Could not check whether a Conditional Access policy blocks device code for this account.
        If the code is refused with <code>AADSTS530036</code>, use the browser sign-in instead.
      </p>
    );
  }

  if (state.kind === 'clear') {
    if (!prominent) return null;
    return (
      <p className="font-display text-[12.5px]" style={{ color: STATUS.successText }} data-testid="device-code-probe">
        No Conditional Access policy blocking device code was found for this account.
      </p>
    );
  }

  const { covers, mayCover, reportOnly } = state;
  return (
    <div className="flex flex-col gap-2" data-testid="device-code-probe">
      {covers.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Device code is blocked for this account</MessageBarTitle>
            {covers.length === 1 ? 'Policy ' : 'Policies '}
            <PolicyNames policies={covers} /> {covers.length === 1 ? 'blocks' : 'block'} the
            device-code flow for the minting account. Exclude the account from{' '}
            {covers.length === 1 ? 'it' : 'them'}, or use the browser sign-in.
          </MessageBarBody>
        </MessageBar>
      )}
      {covers.length === 0 && mayCover.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Device code may be blocked for this account</MessageBarTitle>
            {mayCover.length === 1 ? 'Policy ' : 'Policies '}
            <PolicyNames policies={mayCover} /> may cover the minting account — the check could not
            fully decide its scope. If the code is refused with <code>AADSTS530036</code>, exclude
            the account or use the browser sign-in.
          </MessageBarBody>
        </MessageBar>
      )}
      {covers.length > 0 && mayCover.length > 0 && (
        <p className="font-display text-[12.5px]" style={{ color: TEXT.secondary }}>
          Also may apply: <PolicyNames policies={mayCover} />.
        </p>
      )}
      {reportOnly.length > 0 && (
        <p className="font-display text-[12.5px]" style={{ color: TEXT.secondary }}>
          <PolicyNames policies={reportOnly} /> only {reportOnly.length === 1 ? 'reports' : 'report'}{' '}
          device-code sign-ins (report-only); {reportOnly.length === 1 ? 'it does' : 'they do'} not
          block them.
        </p>
      )}
    </div>
  );
}

/**
 * A browser sign-in finished, but this page was reloaded after it started — so the
 * one-time PKCE key it needs died with the old page. Nothing was minted; start
 * again. Rendered from `useSignInHelper().orphanResult`, never redeemed.
 */
export function ReloadedSignInNotice() {
  return (
    <MessageBar intent="warning" data-testid="reloaded-sign-in">
      <MessageBarBody>
        <MessageBarTitle>That sign-in can no longer be used</MessageBarTitle>
        A browser sign-in finished, but this page was reloaded after it started, so the one-time
        key it needs is gone. Nothing was minted. Start the sign-in again from Activate wave.
      </MessageBarBody>
    </MessageBar>
  );
}
